/**
 * WebAuthn / Passkey client — defensive build.
 *
 * Critical: `@simplewebauthn/browser` is dynamically imported on demand
 * (NOT at module load) because some iOS Safari versions crash with a
 * blank page when certain WebAuthn-related globals are touched during
 * static-analysis / preload. Lazy import means the library is fetched
 * only at the moment the user actively chooses to register or unlock
 * with biometrics.
 *
 * Public API:
 *   isWebAuthnSupported()          — synchronous feature probe (no import)
 *   hasPlatformAuthenticator()     — async feature probe (no import)
 *   registerPasskey(label?)        — full registration ceremony
 *   authenticateWithPasskey()      — assertion ceremony
 *   listPasskeys() / deletePasskey()
 *
 * All calls must be made from inside a user-event handler (click/tap) —
 * browsers refuse to start WebAuthn ceremonies from background contexts.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ax = () => axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// ── Lazy library loader ─────────────────────────────────────────────────
let _lib = null;
let _libPromise = null;

const loadLib = () => {
  if (_lib) return Promise.resolve(_lib);
  if (_libPromise) return _libPromise;
  _libPromise = import('@simplewebauthn/browser')
    .then((mod) => {
      _lib = mod;
      return mod;
    })
    .catch((err) => {
      _libPromise = null;  // allow retry
      throw err;
    });
  return _libPromise;
};

// ── Feature probes that DON'T require the library ───────────────────────

/** Synchronous probe — true when this browser exposes the WebAuthn API.
 *  Uses native globals, so it never imports the library. Safe to call
 *  from render paths. */
export const isWebAuthnSupported = () => {
  try {
    return typeof window !== 'undefined'
      && typeof window.PublicKeyCredential === 'function';
  } catch (_) {
    return false;
  }
};

/** Async probe — true when the device has a built-in biometric authenticator. */
export const hasPlatformAuthenticator = async () => {
  if (!isWebAuthnSupported()) return false;
  try {
    if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
      return false;
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) {
    return false;
  }
};

// ── Ceremonies ─────────────────────────────────────────────────────────

/** Register a new passkey on this device. */
export const registerPasskey = async (label) => {
  if (!isWebAuthnSupported()) {
    const err = new Error('WEBAUTHN_UNSUPPORTED');
    err.code = 'WEBAUTHN_UNSUPPORTED';
    throw err;
  }
  const { startRegistration } = await loadLib();
  const { data: options } = await ax().post('/api/auth/webauthn/register/options');
  const credential = await startRegistration({ optionsJSON: options });
  const { data } = await ax().post('/api/auth/webauthn/register/verify', {
    credential,
    label: label || undefined,
  });
  return data;
};

/** Run the assertion ceremony — returns true on success. */
export const authenticateWithPasskey = async () => {
  if (!isWebAuthnSupported()) {
    const err = new Error('WEBAUTHN_UNSUPPORTED');
    err.code = 'WEBAUTHN_UNSUPPORTED';
    throw err;
  }
  const { startAuthentication } = await loadLib();
  const { data } = await ax().post('/api/auth/webauthn/authenticate/options');
  if (!data?.has_credentials) {
    const err = new Error('NO_CREDENTIALS');
    err.code = 'NO_CREDENTIALS';
    throw err;
  }
  const assertion = await startAuthentication({ optionsJSON: data.options });
  const { data: verify } = await ax().post('/api/auth/webauthn/authenticate/verify', {
    credential: assertion,
  });
  return !!verify?.ok;
};

/** List registered passkeys for the Settings panel. */
export const listPasskeys = async () => {
  try {
    const { data } = await ax().get('/api/auth/webauthn/credentials');
    return data?.credentials || [];
  } catch (_) {
    // Endpoint may be 404 on stale production builds — treat as "no credentials".
    return [];
  }
};

/** Revoke a passkey by id. */
export const deletePasskey = async (id) => {
  await ax().delete(`/api/auth/webauthn/credentials/${id}`);
};
